import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Stack, Grid, Fab, Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadExecutiveKPIs, loadAllTrips, setFilter,
} from '../../reduxcomponents/slices/tripManagementSlice';
import { useMemo } from 'react';
import { ExecutiveKPICards } from './components/KPICards';
import TripsTable from './components/TripsTable';
import RunningTripsMonitor from './components/RunningTripsMonitor';
import PaymentDashboard from './components/PaymentDashboard';
import NotificationCenter from './components/NotificationCenter';
import TripDetailDrawer from './components/TripDetailDrawer';
import { RaiseEscalationDialog } from './components/EscalationDialog';

const TABS = [
  { label: '📋 My Trips',  value: 'trips' },
  { label: '🏃 Running',   value: 'running' },
  { label: '💰 Payments',  value: 'payments' },
];

const ExecutiveDashboard = () => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(s => s.tokens?.user?._id);
  const { executiveKPIs, trips, loading, filters } = useSelector(s => s.tripManagement);

  // Filter to only the executive's own assigned trips
  const myTrips = useMemo(() => ({
    ...trips,
    data: (trips.data || []).filter(t =>
      (t.manage_teams || []).some(m => {
        const uid = m.user_id?._id || m.user_id;
        return uid?.toString() === currentUserId?.toString();
      })
    ),
  }), [trips, currentUserId]);
  const [tab, setTab] = useState('trips');
  const [detailDrawer, setDetailDrawer] = useState({ open: false, trip: null });
  const [escalateDialog, setEscalateDialog] = useState({ open: false, trip: null });

  const refresh = () => {
    dispatch(loadExecutiveKPIs());
    dispatch(loadAllTrips(filters));
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    dispatch(loadAllTrips(filters));
  }, [filters]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>My Operations Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Manage your assigned trips and follow-ups</Typography>
        </Box>
        <Tooltip title="Refresh">
          <Fab size="small" color="primary" onClick={refresh} disabled={loading.kpis || loading.trips}>
            <Refresh />
          </Fab>
        </Tooltip>
      </Stack>

      {/* KPI Cards */}
      <Box mb={3}>
        <ExecutiveKPICards data={executiveKPIs} loading={loading.kpis} />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={9}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              {TABS.map(t => (
                <Tab key={t.value} label={t.label} value={t.value} sx={{ fontWeight: 600, fontSize: 13 }} />
              ))}
            </Tabs>

            <Box p={2.5}>
              {tab === 'trips' && (
                <TripsTable
                  trips={myTrips.data}
                  pagination={{ ...myTrips.pagination, total: myTrips.data.length }}
                  loading={loading.trips}
                  currentStatus={filters.status}
                  onPageChange={(p, l) => dispatch(setFilter({ page: p, ...(l ? { limit: l } : {}) }))}
                  onStatusFilter={(s) => dispatch(setFilter({ status: s, page: 1 }))}
                  onRowClick={(trip) => setDetailDrawer({ open: true, trip })}
                  showAssign={false}
                />
              )}

              {tab === 'running' && (
                <RunningTripsMonitor
                  onEscalate={(trip) => setEscalateDialog({ open: true, trip })}
                  onCall={(trip) => window.open(`tel:${trip.guest_id?.phone}`)}
                />
              )}

              {tab === 'payments' && <PaymentDashboard />}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <NotificationCenter />
          </Paper>
        </Grid>
      </Grid>

      <TripDetailDrawer
        open={detailDrawer.open}
        trip={detailDrawer.trip}
        onClose={() => setDetailDrawer({ open: false, trip: null })}
        isManager={false}
      />

      <RaiseEscalationDialog
        open={escalateDialog.open}
        tripId={escalateDialog.trip?._id}
        onClose={() => setEscalateDialog({ open: false, trip: null })}
        onSuccess={() => dispatch(loadAllTrips(filters))}
      />
    </Box>
  );
};

export default ExecutiveDashboard;
